import { Link } from 'react-router-dom';
import { useScrollReveal, revealClass } from '../../../hooks/useScrollReveal';
import { publications as allPublications } from '../../../mocks/publications';

const typeColors: Record<string, string> = {
  'Book': 'bg-amber-100 text-amber-800',
  'Journal Article': 'bg-emerald-100 text-emerald-800',
  'Magazine Publication': 'bg-rose-100 text-rose-800',
  'Gazette': 'bg-violet-100 text-violet-800',
  'Essay': 'bg-sky-100 text-sky-800',
  'Catalogue': 'bg-orange-100 text-orange-800',
};

// Show the 6 most recent publications that have cover images
const featured = allPublications
  .filter((p) => !!p.coverImage)
  .sort((a, b) => Number(b.year) - Number(a.year))
  .slice(0, 6);

export default function PublicationsSection() {
  const { ref: headingRef, isVisible: headingVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    <section id="publications" className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headingRef} className={`text-center mb-16 ${revealClass(headingVisible)}`}>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">SCHOLARLY OUTPUT</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Books &amp; Publications
          </h2>
          <p className="font-sans text-base text-gray-600 max-w-2xl mx-auto">
            A selection of books, gazettes, and editorial publications contributing to the preservation and celebration of Nigerian cultural heritage and history.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          {featured.map((pub, i) => (
            <Link
              to={`/publications/${pub.id}`}
              key={pub.id}
              className={`bg-[#f9f6f1] rounded-lg overflow-hidden hover:bg-[#f3ede4] transition-colors duration-300 cursor-pointer flex flex-col self-start group ${revealClass(gridVisible, Math.min(i * 100, 400))}`}
            >
              {/* Cover image */}
              <div className="w-full bg-stone-200 overflow-hidden flex-shrink-0">
                <img
                  src={pub.coverImage}
                  alt={pub.title}
                  className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeColors[pub.type] ?? 'bg-gray-100 text-gray-700'}`}>
                    {pub.type}
                  </span>
                  <span className="text-xs text-gray-400 font-sans">{pub.year}</span>
                </div>
                <h3 className="font-serif text-lg font-semibold text-gray-900 mb-3 leading-snug">
                  {pub.title}
                </h3>
                <p className="font-sans text-sm text-gray-600 leading-relaxed line-clamp-3">
                  {pub.abstract}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA to full publications page */}
        <div className={`text-center mt-14 ${revealClass(gridVisible, 300)}`}>
          <Link
            to="/publications"
            className="inline-flex items-center gap-2 font-sans text-sm font-medium bg-gray-900 text-white px-8 py-3.5 rounded-md hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Publications
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
